import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ErrorOutlineOutlined } from '@mui/icons-material';
import { Grid } from '@mui/material';

import * as colors from 'commons/styles/colors';
import { ISTOReceiveItemWithSummary } from 'commons/types';
import {
  completeSTOPickAction,
  completeSTOReceiveAction,
  getSTOAssignedPickItems,
  getSTOReceiveAssignedItems,
} from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';
import { AppDispatch } from 'redux-stores/store';
import { STO_RECEIVE_ROUTES } from 'routes/constants';

import { STOSkuReceiveList } from './components/STOSkuList.component';
import { STOReceiveSummaryPageStyle as S } from './STOReceiveSummaryPage.style';

export const STOReceiveSummaryPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { assignedSTOItems } = useSelector(
    (state: RootReducerInterface) => state.stoReceiveItem,
  );

  useEffect(() => {
    if (assignedSTOItems.length === 0) {
      dispatch(getSTOReceiveAssignedItems());
    }
  }, [dispatch]);

  const { completeSku, incompleteSku } = useMemo(() => {
    function _skuSort(
      sku1: ISTOReceiveItemWithSummary,
      sku2: ISTOReceiveItemWithSummary,
    ) {
      // sort by quantity
      if (sku1.receivedQty < sku2.receivedQty) return -1;
      if (sku1.receivedQty > sku2.receivedQty) return 1;

      // sort by sku code
      return sku1.sku_code < sku2.sku_code ? -1 : 1;
    }
    const _completeSku: ISTOReceiveItemWithSummary[] = [];
    const _incompleteSku: ISTOReceiveItemWithSummary[] = [];

    assignedSTOItems.forEach((stoItem) => {
      const receivedQty = stoItem.receive_actual.reduce(
        (total, { quantity }) => {
          return total + quantity;
        },
        0,
      );

      if (receivedQty === stoItem.quantity) {
        _completeSku.push({ ...stoItem, receivedQty });
      } else {
        _incompleteSku.push({ ...stoItem, receivedQty });
      }
    });

    return {
      completeSku: _completeSku.sort(_skuSort),
      incompleteSku: _incompleteSku.sort(_skuSort),
    };
  }, [assignedSTOItems]);

  const handleBackToDestination = (): void => {
    navigate(STO_RECEIVE_ROUTES.LIST);
  };

  const handleCompleteStockTransfer = (): void => {
    const itemParams = assignedSTOItems.map((item) => ({
      sto_number: item.sto_number,
      sku_code: item.sku_code,
    }));
    dispatch(
      completeSTOReceiveAction({
        payload: {
          items: itemParams,
          pallet: assignedSTOItems[0].pallet,
          box_number: assignedSTOItems[0].box_number,
        },
        postAction: () => navigate(STO_RECEIVE_ROUTES.ASSIGN),
      }),
    );
  };

  return (
    <>
      <S.ContentWrapper>
        {/* incomplete */}
        {incompleteSku.length > 0 && (
          <STOSkuReceiveList
            stoItems={incompleteSku}
            headerText="Incomplete Stock Transfer"
            headerColor={colors.PRIMARY_YELLOW}
            headerIcon={<ErrorOutlineOutlined />}
            defaultExpanded
          />
        )}

        {/* complete */}
        {completeSku.length > 0 && (
          <STOSkuReceiveList
            stoItems={completeSku}
            headerText="Complete Stock Transfer"
          />
        )}
        <S.BlankWhiteSpace />
      </S.ContentWrapper>

      {/* Footer */}
      <S.FooterWrapper>
        <Grid container>
          <Grid item xs={6}>
            <S.SecondaryButton
              onClick={handleBackToDestination}
              data-testid="BtnSTSummaryGoBack"
            >
              GO BACK
            </S.SecondaryButton>
          </Grid>
          <Grid item xs={6}>
            <S.PrimaryButton
              disabled={incompleteSku.length > 0}
              onClick={handleCompleteStockTransfer}
              data-testid="BtnSTSummaryComplete"
            >
              COMPLETE TASK
            </S.PrimaryButton>
          </Grid>
        </Grid>
      </S.FooterWrapper>
    </>
  );
};
